import { useState, useEffect, useCallback } from 'react'
import {
  Star, Search, ChevronRight, Loader, AlertCircle, Plus,
  Tag, Clock, Layers, Shield, X, CheckCircle
} from 'lucide-react'
import type { Rubric, RubricDimension, Competency, ApiResponse } from '../engine/types'
import { listRubrics, createRubric } from '../engine/RubricDesigner'
import { listCompetencies } from '../engine/CompetencyStudio'
import { useLang } from '../i18n/LangContext'

const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
  active:     { bg: 'rgba(74,222,128,0.12)', text: 'var(--color-success)', label: 'Active' },
  draft:      { bg: 'rgba(251,191,36,0.12)', text: 'var(--color-warning)', label: 'Draft' },
  deprecated: { bg: 'rgba(248,113,113,0.12)', text: 'var(--color-danger)', label: 'Deprecated' },
}

function formatRuleType(type: string): string {
  return type.replace(/_/g, ' ')
}

function formatParams(params: Record<string, unknown>): string {
  const entries = Object.entries(params)
  if (entries.length === 0) return ''
  return entries
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(', ')
}

function countCriteria(rubric: Rubric): number {
  return rubric.dimensions.reduce((sum, d) => sum + d.criteria.length, 0)
}

function countRules(rubric: Rubric): number {
  return rubric.dimensions.reduce(
    (sum, d) => sum + d.criteria.reduce((s, c) => s + c.detection_rules.length, 0),
    0
  )
}

export default function RubricDesignerPage() {
  const { t, lang } = useLang()
  const [rubrics, setRubrics] = useState<Rubric[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // Create modal state
  const [showCreate, setShowCreate] = useState(false)
  const [competencies, setCompetencies] = useState<Competency[]>([])
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [newRubric, setNewRubric] = useState({ name: '', competency_id: '', description: '' })

  const loadRubrics = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await listRubrics()
      setRubrics(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rubrics')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadRubrics()
    listCompetencies().then(setCompetencies).catch(() => {})
  }, [loadRubrics])

  const filtered = rubrics.filter(r => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      r.name.toLowerCase().includes(q) ||
      r.competency_id.toLowerCase().includes(q) ||
      r.status.toLowerCase().includes(q) ||
      r.dimensions.some(d =>
        d.name.toLowerCase().includes(q) ||
        d.name_en.toLowerCase().includes(q)
      )
    )
  })

  function toggleExpand(id: string) {
    setExpandedId(prev => (prev === id ? null : id))
  }

  function dimName(dim: RubricDimension): string {
    return lang === 'zh' ? dim.name : dim.name_en
  }

  async function handleCreate() {
    if (!newRubric.name.trim() || !newRubric.competency_id) {
      setCreateError(lang === 'zh' ? '请填写名称和所属能力模型' : 'Name and competency are required')
      return
    }
    try {
      setCreating(true)
      setCreateError(null)
      await createRubric({
        name: newRubric.name.trim(),
        competency_id: newRubric.competency_id,
        status: 'draft',
        version: 1,
        dimensions: [],
      })
      setShowCreate(false)
      setNewRubric({ name: '', competency_id: '', description: '' })
      await loadRubrics()
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create rubric')
    } finally {
      setCreating(false)
    }
  }

  if (loading) {
    return (
      <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3">
            <Loader size={32} className="animate-spin" style={{ color: 'var(--color-accent)' }} />
            <span style={{ color: 'var(--color-text-secondary)' }}>{t('common.loading')}</span>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div
            className="flex flex-col items-center gap-3 max-w-md text-center p-6 rounded-xl"
            style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}
          >
            <AlertCircle size={32} style={{ color: 'var(--color-danger)' }} />
            <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {t('common.error')}
            </h2>
            <p style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
            <button
              onClick={loadRubrics}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            {t('rubric.title')}
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            {t('rubric.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
        >
          <Plus size={16} />
          {t('rubric.new')}
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }} />
        <input
          type="text"
          placeholder={t('common.search')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm outline-none transition-colors"
          style={{
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-primary)',
          }}
          onFocus={e => { e.currentTarget.style.borderColor = 'var(--color-accent)' }}
          onBlur={e => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
        />
      </div>

      {/* Rubric List */}
      {filtered.length === 0 ? (
        <div className="rounded-xl border p-12 text-center" style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}>
          <Star size={32} style={{ color: 'var(--color-text-muted)', margin: '0 auto' }} />
          <p className="mt-3 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {searchQuery ? 'No rubrics match your search.' : 'No rubrics defined yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(rubric => {
            const status = statusConfig[rubric.status] ?? statusConfig.draft
            const isExpanded = expandedId === rubric.id
            const criteriaCount = countCriteria(rubric)
            const rulesCount = countRules(rubric)
            const totalWeight = rubric.dimensions.reduce((s, d) => s + d.weight, 0)

            return (
              <div
                key={rubric.id}
                className="rounded-xl border transition-colors"
                style={{
                  backgroundColor: 'var(--color-bg-card)',
                  borderColor: isExpanded ? 'var(--color-accent)' : 'var(--color-border)',
                }}
              >
                {/* Card Header */}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap mb-1">
                        <h3 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                          {rubric.name}
                        </h3>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{ backgroundColor: status.bg, color: status.text }}
                        >
                          {status.label}
                        </span>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full font-mono"
                          style={{ backgroundColor: 'var(--color-accent-glow)', color: 'var(--color-accent)' }}
                        >
                          v{rubric.version}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Tag size={12} style={{ color: 'var(--color-text-muted)' }} />
                        <span className="text-xs font-mono" style={{ color: 'var(--color-text-muted)' }}>
                          {rubric.competency_id}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleExpand(rubric.id)}
                      className="p-2 rounded-lg transition-colors shrink-0"
                      style={{ color: 'var(--color-text-secondary)' }}
                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; e.currentTarget.style.color = 'var(--color-accent)' }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--color-text-secondary)' }}
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <ChevronRight
                        size={15}
                        className="transition-transform"
                        style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                      />
                    </button>
                  </div>

                  {/* Dimension Weight Bars */}
                  {rubric.dimensions.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {rubric.dimensions.map(dim => {
                        const pct = totalWeight > 0 ? (dim.weight / totalWeight) * 100 : 0
                        return (
                          <div key={dim.id} className="flex items-center gap-1.5">
                            <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                              <div
                                className="h-full rounded-full transition-all"
                                style={{ width: `${pct}%`, backgroundColor: 'var(--color-accent)' }}
                              />
                            </div>
                            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{dimName(dim)}</span>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {/* Stats Row */}
                  <div className="flex items-center gap-6 mt-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    <span className="flex items-center gap-1">
                      <Layers size={12} />{rubric.dimensions.length} Dimensions
                    </span>
                    <span className="flex items-center gap-1">
                      <Star size={12} />{criteriaCount} {t('rubric.criteria')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Shield size={12} />{rulesCount} {t('rubric.detectionRules')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} />{new Date(rubric.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t px-5 py-4" style={{ borderColor: 'var(--color-border)' }}>
                    <div className="space-y-4">
                      {rubric.dimensions.map(dim => {
                        const pct = totalWeight > 0 ? ((dim.weight / totalWeight) * 100).toFixed(1) : '0.0'
                        return (
                          <div
                            key={dim.id}
                            className="rounded-lg p-4 border"
                            style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border)' }}
                          >
                            {/* Dimension Header */}
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                                    {dimName(dim)}
                                  </span>
                                  <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{dim.name_en}</span>
                                </div>
                                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>{dim.description}</p>
                              </div>
                              <span
                                className="text-xs font-mono px-2 py-0.5 rounded shrink-0"
                                style={{ backgroundColor: 'var(--color-accent-glow)', color: 'var(--color-accent)' }}
                              >
                                {pct}%
                              </span>
                            </div>

                            {/* Criteria */}
                            <div className="mt-3 space-y-2">
                              {dim.criteria.map((crit, idx) => (
                                <div
                                  key={idx}
                                  className="rounded-md p-3 border"
                                  style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
                                >
                                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                    <span
                                      className="text-xs font-medium px-2 py-0.5 rounded"
                                      style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}
                                    >
                                      {t('rubric.level')}: {crit.level}
                                    </span>
                                    <span
                                      className="text-xs font-mono px-2 py-0.5 rounded font-semibold"
                                      style={{ backgroundColor: 'var(--color-accent-glow)', color: 'var(--color-accent)' }}
                                    >
                                      {t('rubric.score')}: {crit.score}
                                    </span>
                                  </div>
                                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{crit.description}</p>

                                  {/* Detection Rules */}
                                  {crit.detection_rules.length > 0 && (
                                    <div className="mt-2">
                                      <div className="flex items-center gap-1.5 mb-1">
                                        <Shield size={11} style={{ color: 'var(--color-text-muted)' }} />
                                        <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
                                          {t('rubric.detectionRules')}
                                        </span>
                                      </div>
                                      <div className="flex flex-col gap-1">
                                        {crit.detection_rules.map((rule, rIdx) => (
                                          <div key={rIdx} className="flex items-start gap-2 text-xs font-mono" style={{ color: 'var(--color-text-muted)' }}>
                                            <span
                                              className="px-1.5 py-0.5 rounded shrink-0"
                                              style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-info)' }}
                                            >
                                              {formatRuleType(rule.type)}
                                            </span>
                                            <span className="break-all">{formatParams(rule.params)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Create Rubric Modal */}
      {showCreate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => setShowCreate(false)}
        >
          <div
            className="rounded-xl border w-full max-w-md p-6 space-y-4"
            style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                {t('rubric.new')}
              </h2>
              <button
                onClick={() => setShowCreate(false)}
                className="p-1 rounded-lg transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Name */}
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {lang === 'zh' ? '评分标准名称' : 'Rubric Name'} *
              </label>
              <input
                type="text"
                value={newRubric.name}
                onChange={e => setNewRubric(prev => ({ ...prev, name: e.target.value }))}
                placeholder={lang === 'zh' ? '如：旅游规划标准评分' : 'e.g. Travel Planning Rubric'}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-colors"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text-primary)',
                }}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--color-accent)' }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
              />
            </div>

            {/* Competency */}
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                {lang === 'zh' ? '所属能力模型' : 'Competency'} *
              </label>
              <select
                value={newRubric.competency_id}
                onChange={e => setNewRubric(prev => ({ ...prev, competency_id: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none cursor-pointer transition-colors"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text-primary)',
                }}
              >
                <option value="">{lang === 'zh' ? '请选择能力模型' : 'Select competency'}</option>
                {competencies.map(c => (
                  <option key={c.id} value={c.id}>{c.name} ({c.name_cn})</option>
                ))}
              </select>
            </div>

            {/* Error */}
            {createError && (
              <div
                className="flex items-center gap-2 text-sm p-3 rounded-lg"
                style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: 'var(--color-danger)' }}
              >
                <AlertCircle size={15} />
                {createError}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)',
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border)',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
              >
                {creating ? <Loader size={15} className="animate-spin" /> : <CheckCircle size={15} />}
                {creating ? (lang === 'zh' ? '创建中...' : 'Creating...') : (lang === 'zh' ? '创建' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
